import { ulid } from 'ulid';
import { ApiClient } from './api/client';
import type { TokenSource } from './api/client';
import { Mirror } from './mirror/mirror';
import { Sync } from './mirror/sync';
import type { ConnectionState } from './mirror/sync';
import { Outbox } from './outbox/outbox';
import type { OutboxSettlement } from './outbox/outbox';
import { settlementKind } from './outbox/intents';
import type { AppDb } from './db/schema';
import { clearDevice } from './db/schema';
import type { AuthProvider } from './auth/provider';
import { Appointment, Blackout, ClientDetail, HoursWritten, Preference, Service } from './wire';
import type { Appointment as AppointmentType, Session } from './wire';
import { isoDayIn } from './mirror/time';

/**
 * The composition root, and the app's one observable.
 *
 * React is not involved here on purpose. Everything §8 promises — the mirror,
 * the outbox, ordering, replay, conflicts becoming tasks — lives in plain
 * classes that a test can drive with a fake `fetch` and `fake-indexeddb` and no
 * renderer at all. The views subscribe to `revision` and re-read; that is the
 * entire binding, and it is why the crown-jewel tests are about semantics rather
 * than about pixels.
 */

export interface Runtime {
  db: AppDb;
  api: ApiClient;
  mirror: Mirror;
  outbox: Outbox;
  sync: Sync;
  auth: AuthProvider;
  /**
   * The app's clock, in one place.
   *
   * A view that called `Date.now()` directly would be a view whose "today" the
   * mirror's day index could not be made to agree with — and it would be a view
   * no test could pin to a date. One clock, injected, used by the mirror, the
   * outbox and the calendar alike.
   */
  now(): number;
  /** Bumped on every change to the mirror, the outbox or the connection. */
  revision(): number;
  subscribe(listener: () => void): () => void;
  connection(): ConnectionState;
  signOut(): Promise<void>;
}

export interface RuntimeOptions {
  db: AppDb;
  auth: AuthProvider;
  baseUrl: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  pastDays?: number;
  futureDays?: number;
  chunkDays?: number;
}

export function createRuntime(options: RuntimeOptions): Runtime {
  let revision = 0;
  const listeners = new Set<() => void>();
  const bump = (): void => {
    revision += 1;
    for (const listener of listeners) listener();
  };

  const getToken: TokenSource = (force) => options.auth.token(force);
  const api = new ApiClient({
    baseUrl: options.baseUrl,
    getToken,
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
  });

  const mirror = new Mirror({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
    ...(options.pastDays === undefined ? {} : { pastDays: options.pastDays }),
    ...(options.futureDays === undefined ? {} : { futureDays: options.futureDays }),
    ...(options.chunkDays === undefined ? {} : { chunkDays: options.chunkDays }),
  });

  const outbox = new Outbox({
    db: options.db,
    api,
    onChange: bump,
    ...(options.now === undefined ? {} : { now: options.now }),
    onSettled: (settlement) => applySettlement(mirror, settlement),
  });

  let connection: ConnectionState = 'offline';
  const sync = new Sync({
    api,
    mirror,
    outbox,
    onState: (state) => {
      connection = state;
      bump();
    },
    ...(options.sleep === undefined ? {} : { sleep: options.sleep }),
  });

  return {
    db: options.db,
    api,
    mirror,
    outbox,
    sync,
    auth: options.auth,
    now: options.now ?? Date.now,
    revision: () => revision,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    connection: () => connection,
    async signOut() {
      sync.stop();
      await options.auth.signOut();
      await clearDevice(options.db);
      bump();
    },
  };
}

/**
 * What a settled intent does to the mirror — including the reconciliation the
 * optimistic row was waiting for.
 *
 * The server's answer carries `client_intent_id` (echoed by `withIntentId` in
 * `core-api`'s `intents.ts`) and the real row. Writing that row *is* the
 * reconciliation: the optimistic copy is not stored in the mirror at all, it is
 * derived from the outbox entry, and the entry has just moved to `done`. So the
 * pending line disappears and the real one appears in the same repaint, without
 * a reconciliation pass that could get the pairing wrong.
 *
 * The `client_intent_id` echo is still checked, because if it ever came back for
 * a *different* intent we would be writing one action's result under another's
 * name — and finding that out from a failed assertion is much cheaper than
 * finding it out from a support call.
 */
async function applySettlement(mirror: Mirror, settlement: OutboxSettlement): Promise<void> {
  const { entry, body } = settlement;
  const session = await mirror.session();
  const timezone = session?.tenant.timezone ?? 'UTC';

  const echoed = (body as { client_intent_id?: unknown } | null)?.client_intent_id;
  if (typeof echoed === 'string' && echoed !== entry.id) {
    throw new Error(
      `core-api echoed client_intent_id ${echoed} for intent ${entry.id}. ` +
        'A response matched to the wrong intent would reconcile the wrong row.',
    );
  }

  switch (settlementKind(entry.kind)) {
    case 'appointment': {
      const parsed = Appointment.safeParse(body);
      if (parsed.success) await mirror.putAppointment(parsed.data, timezone);
      return;
    }
    case 'client': {
      const parsed = ClientDetail.safeParse(body);
      if (parsed.success) await mirror.putClient(parsed.data);
      return;
    }
    case 'preference': {
      // A preference lives inside its client's detail row, so the settled
      // response is not the whole truth about the client — refetch when we can,
      // and let the next hydrate fix it when we cannot.
      const clientId = entry.target['client_id'];
      if (clientId === undefined) return;
      Preference.safeParse(body);
      await mirror.refetchClient(clientId).catch(() => undefined);
      return;
    }
    case 'service': {
      const parsed = Service.safeParse(body);
      if (parsed.success) await mirror.putService(parsed.data);
      return;
    }
    case 'hours': {
      const parsed = HoursWritten.safeParse(body);
      if (parsed.success) {
        await mirror.putHours({ timezone, hours: parsed.data.hours });
      }
      return;
    }
    case 'blackout': {
      const parsed = Blackout.safeParse(body);
      if (parsed.success) await mirror.putBlackout(parsed.data);
      return;
    }
    case 'none':
      return;
  }
}

/**
 * The optimistic appointment: what the owner sees the instant they tap Book.
 *
 * Every field the calendar renders is present and every field the *server*
 * decides is a placeholder — `price_cents` is zero, and the id is a local one
 * that no server will ever mint. That asymmetry is the honest one: the device
 * may say "you asked for this", it may not say "this costs $120" (obligation
 * B7 — prices come from the catalog, and the catalog on this device may be a
 * week old).
 *
 * `source: 'staff'` or `'walk_in'` matches what the request asks for, so the row
 * does not visibly change category when it lands.
 */
export function optimisticAppointment(input: {
  serviceId: string;
  serviceName: string;
  resourceId: string;
  resourceName: string | null;
  startIso: string;
  durationMin: number;
  clientId: string | null;
  clientName: string;
  source: 'staff' | 'walk_in';
  partySize?: number;
  notes?: string | undefined;
  nowIso: string;
}): AppointmentType {
  const start = Date.parse(input.startIso);
  return {
    appointment_id: `local_${ulid()}`,
    status: 'confirmed',
    source: input.source,
    slot: {
      start: input.startIso,
      end: new Date(start + input.durationMin * 60_000).toISOString(),
      resource_id: input.resourceId,
    },
    resource_name: input.resourceName,
    service_id: input.serviceId,
    service_name: input.serviceName,
    duration_min: input.durationMin,
    party_size: input.partySize ?? 1,
    price_cents: 0,
    discount_cents: 0,
    value_cents: 0,
    notes: input.notes ?? null,
    provider_ref: null,
    client: {
      client_id: input.clientId ?? 'local_pending',
      display_name: input.clientName,
      phone: null,
    },
    created_at: input.nowIso,
    updated_at: input.nowIso,
  };
}

/** The local day an optimistic row belongs to, in the tenant's zone. */
export function optimisticDay(appointment: AppointmentType, session: Session | null): string {
  return isoDayIn(session?.tenant.timezone ?? 'UTC', appointment.slot.start);
}
